import { ListItemIcon, ListItemText, MenuItem, Stack } from "@mui/material";
import { ReactNode } from "react";

import { AppPermission, VehicleDamageDto } from "@/core/api/generated/v1";

import AuthorizedMenuItem from "../../Auth/AuthorizedMenuItem";
import AuthorizedTenantElement from "../../Auth/AuthorizedTenantElement";
import EntityMenu from "../../EntityMenu/EntityMenu";
import AppIcon from "../../Icons/AppIcon";
import { RepairOperationAddVehicleDamagesModal } from "../RepairOperation/RepairOperationAddVehicleDamagesModal";
import VehicleDamageDeleteModal from "./VehicleDamageDeleteModal";
import VehicleDamageMigrateToNewVisualModelModal from "./VehicleDamageMigrateToNewVisualModelModal";
import VehicleDamageViewModal from "./View/VehicleDamageViewModal";

enum ActionFlags {
  Assessments = "Assessments",
  QuickView = "QuickView",
  AddToRepairOperation = "AddToRepairOperation",
  MigrateToNewVisualModel = "MigrateToNewVisualModel",
  Delete = "Delete",
}

const defaultDisplayProps = {
  actions: {
    quickView: true,
    addToRepairOperation: true,
    migrateToNewVisualModel: true,
    delete: true,
  },
};

interface TriggersProps {
  entity?: VehicleDamageDto | null;
  definition: typeof ActionFlags;
  handleClose: () => void;
  startAction: (props: ActionFlags) => void;
}

interface Props {
  entity: VehicleDamageDto;
  disabled?: boolean;
  displayProps?: Partial<typeof defaultDisplayProps>;
  onUpdate: ((newValue: Nil<VehicleDamageDto>) => void) | undefined;
  onDelete: ((newValue: Nil<VehicleDamageDto>) => void) | undefined;
  customActions?: (props: TriggersProps) => ReactNode;
}

export function VehicleDamageMenu({
  entity,
  disabled,
  customActions,
  onUpdate,
  onDelete,
  displayProps = defaultDisplayProps,
}: Props) {
  displayProps = {
    ...defaultDisplayProps,
    ...displayProps,
  };

  return (
    <EntityMenu
      definition={ActionFlags}
      entity={entity}
      disabled={disabled}
      actionTriggers={({ handleClose, startAction }) => (
        <>
          {customActions &&
            customActions({
              entity,
              definition: ActionFlags,
              handleClose,
              startAction,
            })}

          {displayProps?.actions?.quickView && (
            <MenuItem onClick={() => startAction(ActionFlags.QuickView)}>
              <ListItemIcon>
                <AppIcon of='quickView' fontSize='small' />
              </ListItemIcon>
              <ListItemText>Quick view</ListItemText>
            </MenuItem>
          )}
          {displayProps?.actions?.addToRepairOperation && (
            <AuthorizedTenantElement tenantIds={[entity?.tenantId]}>
              <AuthorizedMenuItem
                permissions={[AppPermission.FleetAppAccess]}
                permissionsAny={[
                  AppPermission.RepairOperationPerform,
                  AppPermission.RepairOperationManage,
                ]}
                onClick={() => startAction(ActionFlags.AddToRepairOperation)}
              >
                <ListItemIcon>
                  <AppIcon of='add' fontSize='small' />
                </ListItemIcon>
                <ListItemText>Add to repair operation</ListItemText>
              </AuthorizedMenuItem>
            </AuthorizedTenantElement>
          )}
          {displayProps?.actions?.migrateToNewVisualModel && (
            <AuthorizedTenantElement tenantIds={[entity?.tenantId]}>
              <AuthorizedMenuItem
                permissions={[AppPermission.FleetAppAccess, AppPermission.VehicleDamageManage]}
                onClick={() => startAction(ActionFlags.MigrateToNewVisualModel)}
              >
                <ListItemIcon>
                  <AppIcon of='edit' fontSize='small' />
                </ListItemIcon>
                <ListItemText>Migrate to new visual model</ListItemText>
              </AuthorizedMenuItem>
            </AuthorizedTenantElement>
          )}
          {displayProps?.actions?.delete && (
            <AuthorizedTenantElement tenantIds={[entity?.tenantId]}>
              <AuthorizedMenuItem
                permissions={[AppPermission.FleetAppAccess, AppPermission.VehicleDamageManage]}
                onClick={() => startAction(ActionFlags.Delete)}
              >
                <ListItemIcon>
                  <AppIcon of='delete' fontSize='small' />
                </ListItemIcon>
                <ListItemText>Delete</ListItemText>
              </AuthorizedMenuItem>
            </AuthorizedTenantElement>
          )}
        </>
      )}
      actionHandlers={({ currentAction, cancelAction, completeAction }) => (
        <Stack>
          {/* Quick view */}
          {entity && (
            <VehicleDamageViewModal
              open={currentAction === ActionFlags.QuickView}
              onClose={() => cancelAction()}
              viewProps={{
                vehicleId: entity.vehicle?.id,
                vehicleDamageId: entity.id,
                vehicleDamage: entity,
                onDelete: null,
              }}
            />
          )}

          {/* Add to repair operation*/}
          {entity && (
            <RepairOperationAddVehicleDamagesModal
              open={currentAction === ActionFlags.AddToRepairOperation}
              onClose={() => cancelAction()}
              vehicleId={entity.vehicle?.id || ""}
              vehicleDamageIdsCosts={undefined}
              vehicleDamageIds={[entity.id || ""]}
              onSaved={() => {
                onUpdate && onUpdate(undefined);
              }}
            />
          )}

          {/* Migrate to new visual model */}
          {entity && (
            <VehicleDamageMigrateToNewVisualModelModal
              open={currentAction === ActionFlags.MigrateToNewVisualModel}
              onClose={() => cancelAction()}
              migrateProps={{
                vehicleDamage: entity,
                onCancel: () => {
                  completeAction();
                },
                onSave: (newValue) => {
                  onUpdate && onUpdate(newValue);
                  completeAction();
                },
              }}
            />
          )}

          {/* Delete */}
          {entity && (
            <VehicleDamageDeleteModal
              entity={entity}
              open={currentAction === ActionFlags.Delete}
              onClose={() => cancelAction()}
              onDelete={() => {
                onDelete && onDelete(entity);
                completeAction();
              }}
            />
          )}
        </Stack>
      )}
    />
  );
}
